import { NavLink } from 'react-router-dom';
import { TransitionValue, useTransition } from 'react-use-transition';
import styled, { css } from 'styled-components';
import {
  IconArrowRight,
  IconChevronDown,
  IconChevronUp,
  IconHeartAlt,
  IconGears,
  IconHelpAlt,
  IconMessages,
} from '@spotify-internal/encore-web';
import { BadgeWithText } from 'components/Badge';
import { useViewport, Viewport } from 'libs/utils/useViewport';
import { useCurrentUser } from 'libs/services/s4p';
import { generalFeedbackLink } from 'libs/utils/externalUrls';
import {
  UserControlsToggle,
  UserItem,
  UserLink,
  UserLinkTitle,
  UserControlsList,
} from 'components/SidePanel';
import { useSidePanel, hideUserControls, showUserControls } from './SidePanelState';
import gTagManager from 'libs/services/logging';
import { useT } from '@spotify-internal/i18n-core';

type UserControlsListWrapperProps = {
  listHeight: number;
  transitionState: TransitionValue;
};

const transition = {
  entering: css`
    margin-bottom: 0;
    visibility: visible;
    transition: margin-bottom 0.1s ease-in;
  `,

  entered: css`
    margin-bottom: 0;
    visibility: visible;
  `,

  exiting: css`
    margin-bottom: -${(props: UserControlsListWrapperProps) => props.listHeight}px;
    visibility: hidden;
    transition: margin-bottom 0.1s ease-in;
  `,

  exited: css`
    margin-bottom: -${(props: UserControlsListWrapperProps) => props.listHeight}px;
    visibility: hidden;
  `,
};

const UserControlsListWrapper = styled.div<UserControlsListWrapperProps>`
  overflow: auto;
  ${({ transitionState }) => transition[transitionState]}
`;

const UserControlsBadgeWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  width: 100%;
  height: 100%;
`;

const StyledUserLink = styled(UserLink)`
  text-decoration: none;
`;

export function SidePanelFooter() {
  const t = useT();
  const [{ shouldShowUserControls }, sidePanelDispatch] = useSidePanel();
  const xs = useViewport() === Viewport.XS;
  const user = useCurrentUser();

  const [transitionState, handleTransitionEnd] = useTransition(shouldShowUserControls);

  if (!user) {
    return null;
  }

  /* TODO - find a better way to grab the height of UserList */
  const userListItems = 6;
  const userListHeight = userListItems * 64;

  return (
    <>
      <UserControlsToggle
        aria-controls="navigation-user-controls"
        aria-expanded={shouldShowUserControls}
        onClick={() => {
          sidePanelDispatch(shouldShowUserControls ? hideUserControls() : showUserControls());
          gTagManager({
            event: 'openUserControls',
          });
        }}
        title={t(
          'L_SIDEPANEL_0',
          'Show user controls',
          'Link label for use controls list in side panel',
        )}
      >
        <UserControlsBadgeWrapper>
          <BadgeWithText variant="user" text={user.fullName} secondaryText={user.email} />
          {shouldShowUserControls ? (
            <IconChevronDown iconSize={xs ? undefined : 16} />
          ) : (
            <IconChevronUp iconSize={xs ? undefined : 16} />
          )}
        </UserControlsBadgeWrapper>
      </UserControlsToggle>
      <UserControlsListWrapper
        id="navigation-user-controls"
        transitionState={transitionState}
        onTransitionEnd={handleTransitionEnd}
        listHeight={userListHeight}
      >
        <UserControlsList style={{ margin: 0, padding: 0 }}>
          <UserItem>
            <StyledUserLink
              href="https://www.spotify.com/us/account/overview/"
              title={t(
                'L_SIDEPANEL_1',
                'Profile Settings',
                'Link label for account profile settings',
              )}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                gTagManager({
                  event: 'selectUserControlsLink',
                  userControlsLink: 'Profile Settings',
                });
              }}
            >
              <IconGears />
              <UserLinkTitle>
                {t('HREF_SIDEPANEL_0', 'Settings', 'Link to account profile settings')}
              </UserLinkTitle>
            </StyledUserLink>
          </UserItem>
          <UserItem>
            <StyledUserLink
              href={generalFeedbackLink}
              title={t('L_SIDEPANEL_2', 'Product feedback', 'Link label for product feedback')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                gTagManager({
                  event: 'selectUserControlsLink',
                  userControlsLink: 'Product feedback',
                });
              }}
            >
              <IconMessages />
              <UserLinkTitle>
                {t('HREF_SIDEPANEL_1', 'Product feedback', 'Link to product feedback')}
              </UserLinkTitle>
            </StyledUserLink>
          </UserItem>
          <UserItem>
            <StyledUserLink
              as={NavLink}
              to="/welcome-guide"
              title={t('L_SIDEPANEL_3', 'Welcome guide', 'Link label for welcome guide')}
              onClick={() => {
                gTagManager({
                  event: 'selectUserControlsLink',
                  userControlsLink: 'Welcome guide',
                });
              }}
            >
              <IconHeartAlt />
              <UserLinkTitle>
                {t('HREF_SIDEPANEL_2', 'Welcome guide', 'Link to welcome guide')}
              </UserLinkTitle>
            </StyledUserLink>
          </UserItem>
          <UserItem>
            <StyledUserLink
              as={NavLink}
              to="/faq"
              title={t('L_SIDEPANEL_4', 'FAQ', 'Link label for FAQ (Frequently Asked Questions')}
              onClick={() => {
                gTagManager({
                  event: 'selectUserControlsLink',
                  userControlsLink: 'FAQ',
                });
              }}
            >
              <IconHelpAlt />
              <UserLinkTitle>
                {t('HREF_SIDEPANEL_3', 'FAQ', 'Link to FAQ (Frequently Asked Questions')}
              </UserLinkTitle>
            </StyledUserLink>
          </UserItem>
          <UserItem>
            <StyledUserLink
              href="/logout"
              title={t(
                'L_SIDEPANEL_5',
                'Log out',
                'Label for link to log out of Spotify for Publishing (S4P) application',
              )}
            >
              <IconArrowRight />
              <UserLinkTitle>
                {t(
                  'HREF_SIDEPANEL_4',
                  'Log out',
                  'Link to log out of Spotify for Publishing (S4P) application',
                )}
              </UserLinkTitle>
            </StyledUserLink>
          </UserItem>
        </UserControlsList>
      </UserControlsListWrapper>
    </>
  );
}
